import { createScale, scaleCanBeZeroed } from '@visx/scale';
import { extent as d3Extent } from '@visx/vendor/d3-array';
import { useMemo } from 'react';
import isDiscreteScale from "../utils/isDiscreteScale.js";
/** A hook for creating memoized x- and y-scales. */
export default function useScales(_ref) {
  let {
    dataRegistry,
    xRange,
    xScaleConfig,
    yRange,
    yScaleConfig
  } = _ref;
  // pull out memoization keys that are less likely to change
  const registryKeys = dataRegistry.keys();
  const [xMin, xMax] = xRange;
  const [yMin, yMax] = yRange;
  const memoizedXScale = useMemo(() => {
    const registryEntries = registryKeys.map(key => dataRegistry.get(key));
    const xValues = registryEntries.reduce((combined, entry) => entry ? combined.concat(entry.data.map(d => entry.xAccessor(d))) : combined, []);

    // d3Extent scale returns NaN domain for empty arrays
    if (xValues.length === 0) return undefined;
    const xDomain = isDiscreteScale(xScaleConfig) ? xValues : d3Extent(xValues);
    let xScale = scaleCanBeZeroed(xScaleConfig) ? createScale({
      range: [xMin, xMax],
      domain: xDomain,
      zero: true,
      ...xScaleConfig
    }) : createScale({
      range: [xMin, xMax],
      domain: xDomain,
      ...xScaleConfig
    });

    // apply any scale updates from the registry
    registryEntries.forEach(entry => {
      if (entry?.xScale) xScale = entry.xScale(xScale);
    });
    return xScale;
  }, [dataRegistry, xScaleConfig, registryKeys, xMin, xMax]);

  // same for yScale. this logic is hard to apply generically because of the scale types / accessors
  const memoizedYScale = useMemo(() => {
    const registryEntries = registryKeys.map(key => dataRegistry.get(key));
    const yValues = registryEntries.reduce((combined, entry) => entry ? combined.concat(entry.data.map(d => entry.yAccessor(d))) : combined, []);

    // d3Extent scale returns NaN domain for empty arrays
    if (yValues.length === 0) return undefined;
    const yDomain = isDiscreteScale(yScaleConfig) ? yValues : d3Extent(yValues);
    let yScale = scaleCanBeZeroed(yScaleConfig) ? createScale({
      range: [yMin, yMax],
      domain: yDomain,
      zero: true,
      ...yScaleConfig
    }) : createScale({
      range: [yMin, yMax],
      domain: yDomain,
      ...yScaleConfig
    });

    // apply any scale updates from the registry
    registryEntries.forEach(entry => {
      if (entry?.yScale) yScale = entry.yScale(yScale);
    });
    return yScale;
  }, [dataRegistry, yScaleConfig, registryKeys, yMin, yMax]);
  return {
    xScale: memoizedXScale,
    yScale: memoizedYScale
  };
}